import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

// DEBUG: module-level marker. Fires once per fresh JS module execution.
// If we see only one of these on the post-back navigation but expect two
// (initial load + reload), the page isn't actually doing a full reload.
console.log('[NL] module loaded', { at: new Date().toISOString(), perf: performance.now() });

const SHIRT_MAX_ROWS = 6;
const SHIRT_QTY_MIN = 1;
const SHIRT_QTY_MAX = 10;

class NationalsLanding extends Component {
  requiredRefs = ['cta', 'ctaLabel', 'status', 'summary'];

  #state = {
    entry: { gender: null, division: null, second: null },
    shirt: [], // [{ id: rowId, gender: null|string, size: null|string, qty: number }, ...]
  };
  #nextRowId = 0;

  #entry = null;
  #shirt = null;
  #genderLockMap = {};
  #moneyFormat = '${{amount}}';
  #submitting = false;
  #shirtsOnly = false;
  #pageShowHandler = null;
  #beforeUnloadHandler = null;

  connectedCallback() {
    super.connectedCallback();
    console.log('[NL] connectedCallback start', { perf: performance.now() });

    this.#shirtsOnly = this.dataset.shirtsOnly === 'true';
    this.#entry = this.#parseJSON('nationals-entry-data');
    this.#shirt = this.#parseJSON('nationals-shirt-data');
    this.#genderLockMap = this.#parseJSON('nationals-gender-lock') || {};
    this.#moneyFormat = this.dataset.moneyFormat || this.#moneyFormat;

    console.log('[NL] parsed data', {
      shirtsOnly: this.#shirtsOnly,
      entryPresent: !!this.#entry,
      entryHasDivision: !!(this.#entry && this.#entry.hasDivision),
      entryVariantCount: this.#entry ? this.#entry.variants.length : 0,
      shirtPresent: !!this.#shirt,
      shirtVariantCount: this.#shirt ? this.#shirt.variants.length : 0,
      lockMapSize: Object.keys(this.#genderLockMap).length,
    });

    // In shirts-only mode the entry product is irrelevant — require shirt instead.
    // Otherwise require entry as the primary product.
    if (this.#shirtsOnly) {
      if (!this.#shirt || !Array.isArray(this.#shirt.variants)) {
        console.warn('[nationals-landing] shirts-only mode but shirt product data missing — section will not function');
        return;
      }
    } else {
      if (!this.#entry || !Array.isArray(this.#entry.variants)) {
        console.warn('[nationals-landing] entry product data missing — section will not function');
        return;
      }
      this.#warnIfLockMapEmpty();
    }

    this.addEventListener('change', (event) => this.#handleChange(event));
    this.addEventListener('click', (event) => this.#handleClick(event));
    this.refs.cta.addEventListener('click', () => this.#submit());

    // Reset transient submit state in case element reconnects with stale flags
    // from a previous lifecycle (e.g. back-navigation, section re-render).
    this.#submitting = false;
    this.refs.status.textContent = '';

    // Disable bfcache for this page. Partial state restoration (radio buttons
    // survive in DOM, JS in-memory state doesn't) causes UI/state desync — entry
    // says "choose your division and class" while the division radio is still
    // visibly selected. Forcing a fresh page on Back nav is cleaner than trying
    // to reconcile partial state. Chrome and most browsers disable bfcache when
    // a beforeunload listener is registered, even if the handler is a noop — no
    // event.preventDefault and no return value means no confirmation dialog.
    this.#beforeUnloadHandler = () => {};
    window.addEventListener('beforeunload', this.#beforeUnloadHandler);

    // Secondary safety net: some browsers (notably Safari) may still bfcache
    // despite beforeunload. The pageshow handler resets transient state on
    // bfcache restoration (event.persisted === true).
    this.#pageShowHandler = (event) => {
      console.log('[NL] pageshow', { persisted: event.persisted, perf: performance.now() });
      if (event.persisted) {
        this.#submitting = false;
        this.refs.status.textContent = '';
        this.#render();
      }
    };
    window.addEventListener('pageshow', this.#pageShowHandler);

    if (!this.#shirtsOnly) this.#applyAvailability('entry');
    this.#render();

    // Shirts-only: the shirt IS the entire purchase, so materialise the first
    // row on load. Athletes land with selectors visible — no '+Add a shirt'
    // click required before they can pick anything. They can still add more
    // or remove this row if they want.
    if (this.#shirtsOnly && this.#shirt) {
      this.#addShirtRow();
    }

    console.log('[NL] connectedCallback done', {
      shirtsOnly: this.#shirtsOnly,
      entryGroupPresent: !!this.querySelector('[data-group="entry"]'),
      entryLabelCount: this.querySelectorAll('[data-group="entry"] .nationals-option-value').length,
    });
  }

  disconnectedCallback() {
    console.log('[NL] disconnectedCallback', { perf: performance.now() });
    super.disconnectedCallback();
    if (this.#beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.#beforeUnloadHandler);
      this.#beforeUnloadHandler = null;
    }
    if (this.#pageShowHandler) {
      window.removeEventListener('pageshow', this.#pageShowHandler);
      this.#pageShowHandler = null;
    }
  }

  #parseJSON(id) {
    const el = this.querySelector(`script[type="application/json"][data-key="${id}"]`);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.warn(`[nationals-landing] failed to parse ${id}`, err);
      return null;
    }
  }

  #warnIfLockMapEmpty() {
    const hasParentheticals = this.#entry.variants.some(
      (v) => typeof v.c === 'string' && v.c.includes('(') && v.c.includes(')')
    );
    if (hasParentheticals && Object.keys(this.#genderLockMap).length === 0) {
      console.warn(
        '[nationals-landing] weight class values contain parentheticals but gender-lock map is empty — ' +
          'check the section Liquid pattern in nationals-event-landing.liquid.'
      );
    }
  }

  #handleChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    console.log('[NL] handleChange', {
      name: input.name,
      value: input.value,
      inShirtRow: !!input.closest('[data-row-id]'),
    });

    // Route shirt-row inputs to the per-row handler.
    const row = input.closest('[data-row-id]');
    if (row) {
      this.#handleShirtRowChange(row, input);
      return;
    }

    // Entry inputs (and shirt inputs are no longer rendered outside rows).
    const group = input.closest('[data-group]');
    if (!group) return;
    const groupId = group.dataset.group;
    if (groupId !== 'entry') return;

    const isGender = input.name.endsWith('-gender');
    const isDivision = input.name.endsWith('-division');
    const key = isGender ? 'gender' : isDivision ? 'division' : 'second';
    this.#state.entry[key] = input.value;

    if (isGender || isDivision) {
      this.#applyAvailability('entry');
      const { gender, division, second } = this.#state.entry;
      if (second && !this.#isSecondAllowed('entry', second, gender, division)) {
        this.#state.entry.second = null;
        const stale = group.querySelector(`input[name="entry-second"]:checked`);
        if (stale) stale.checked = false;
      }
    }

    this.#render();
  }

  #handleClick(event) {
    const btn = event.target.closest('[data-action]');
    if (!btn) return;
    if (btn === this.refs.cta) return; // CTA has its own listener

    const action = btn.dataset.action;
    const row = btn.closest('[data-row-id]');
    const rowId = row ? Number(row.dataset.rowId) : null;

    switch (action) {
      case 'add-shirt':
        this.#addShirtRow();
        break;
      case 'remove-shirt':
        if (rowId !== null) this.#removeShirtRow(rowId);
        break;
      case 'increment':
        if (rowId !== null) this.#changeShirtQty(rowId, +1);
        break;
      case 'decrement':
        if (rowId !== null) this.#changeShirtQty(rowId, -1);
        break;
    }
  }

  #applyAvailability(groupId) {
    // Entry only — shirt rows use #applyShirtRowAvailability.
    const product = this.#entry;
    if (groupId !== 'entry' || !product) return;
    const { gender, division } = this.#state.entry;
    const hasDivision = !!product.hasDivision;
    const group = this.querySelector(`[data-group="entry"]`);
    if (!group) {
      console.log('[NL] applyAvailability: no group found');
      return;
    }

    const labels = group.querySelectorAll('.nationals-option-value');
    let hiddenCount = 0;
    let shownCount = 0;
    labels.forEach((label) => {
      const value = label.dataset.value;
      let hidden;
      if (hasDivision && (!gender || !division)) {
        hidden = true;
      } else if (!gender) {
        hidden = !!this.#genderLockMap[value];
      } else {
        hidden = !this.#isSecondAllowed('entry', value, gender, division);
      }
      label.hidden = hidden;
      if (hidden) hiddenCount++;
      else shownCount++;
    });
    console.log('[NL] applyAvailability(entry)', {
      gender,
      division,
      hasDivision,
      totalLabels: labels.length,
      hiddenCount,
      shownCount,
    });
  }

  #isSecondAllowed(groupId, secondValue, gender, division) {
    if (groupId === 'entry') {
      const lock = this.#genderLockMap[secondValue];
      if (lock && lock !== gender.toLowerCase()) return false;
    }
    const product = this.#entry;
    return product.variants.some((v) => {
      if (v.g !== gender || v.c !== secondValue || !v.a) return false;
      if (product.hasDivision && v.d !== division) return false;
      return true;
    });
  }

  #resolveVariant(groupId) {
    // Entry only. Shirt resolution is per-row via #resolveShirtRow.
    if (groupId !== 'entry') return null;
    const product = this.#entry;
    const { gender, division, second } = this.#state.entry;
    if (!gender || !second) return null;
    if (product.hasDivision && !division) return null;
    return (
      product.variants.find((v) => {
        if (v.g !== gender || v.c !== second || !v.a) return false;
        if (product.hasDivision && v.d !== division) return false;
        return true;
      }) || null
    );
  }

  // ---------- Shirt builder ----------

  #shirtBuilder() {
    return this.querySelector('[data-group="shirt"]');
  }

  #addShirtRow() {
    if (!this.#shirt) return;
    if (this.#state.shirt.length >= SHIRT_MAX_ROWS) return;

    const builder = this.#shirtBuilder();
    if (!builder) return;
    const template = builder.querySelector('template[ref="shirtRowTemplate"]');
    const rowsContainer = builder.querySelector('[ref="shirtRows"]');
    if (!template || !rowsContainer) return;

    const rowId = this.#nextRowId++;
    const clone = template.content.firstElementChild.cloneNode(true);
    clone.dataset.rowId = String(rowId);

    // Scope radio names to this row so groups don't collide across rows.
    clone.querySelectorAll('input[data-row-gender]').forEach((input) => {
      input.name = `shirt-${rowId}-gender`;
    });
    clone.querySelectorAll('input[data-row-size]').forEach((input) => {
      input.name = `shirt-${rowId}-size`;
    });

    rowsContainer.appendChild(clone);
    this.#state.shirt.push({ id: rowId, gender: null, size: null, qty: 1 });

    this.#applyShirtRowAvailability(rowId);
    this.#updateShirtAddButton();
    this.#render();
  }

  #removeShirtRow(rowId) {
    const idx = this.#state.shirt.findIndex((r) => r.id === rowId);
    if (idx === -1) return;
    this.#state.shirt.splice(idx, 1);
    const rowEl = this.querySelector(`[data-row-id="${rowId}"]`);
    if (rowEl) rowEl.remove();
    this.#updateShirtAddButton();
    this.#render();
  }

  #changeShirtQty(rowId, delta) {
    const state = this.#state.shirt.find((r) => r.id === rowId);
    if (!state) return;
    const next = Math.max(SHIRT_QTY_MIN, Math.min(SHIRT_QTY_MAX, state.qty + delta));
    if (next === state.qty) return;
    state.qty = next;
    const rowEl = this.querySelector(`[data-row-id="${rowId}"]`);
    if (rowEl) {
      const display = rowEl.querySelector('[data-qty]');
      if (display) display.textContent = String(next);
    }
    this.#render();
  }

  #handleShirtRowChange(rowEl, input) {
    const rowId = Number(rowEl.dataset.rowId);
    const state = this.#state.shirt.find((r) => r.id === rowId);
    if (!state) return;

    if (input.dataset.rowGender !== undefined) {
      state.gender = input.value;
      // If previously chosen size no longer has an available variant for the new gender, clear it.
      if (state.size && !this.#shirtSizeAvailable(state.gender, state.size)) {
        state.size = null;
        const stale = rowEl.querySelector('input[data-row-size]:checked');
        if (stale) stale.checked = false;
      }
      this.#applyShirtRowAvailability(rowId);
    } else if (input.dataset.rowSize !== undefined) {
      state.size = input.value;
    }

    this.#render();
  }

  #shirtSizeAvailable(gender, size) {
    if (!this.#shirt) return false;
    return this.#shirt.variants.some((v) => v.g === gender && v.c === size && v.a);
  }

  #applyShirtRowAvailability(rowId) {
    const rowEl = this.querySelector(`[data-row-id="${rowId}"]`);
    if (!rowEl) return;
    const state = this.#state.shirt.find((r) => r.id === rowId);
    if (!state) return;
    const labels = rowEl.querySelectorAll('.nationals-option-value');
    labels.forEach((label) => {
      const size = label.dataset.value;
      const hidden = state.gender ? !this.#shirtSizeAvailable(state.gender, size) : false;
      label.hidden = hidden;
    });
  }

  #resolveShirtRow(rowId) {
    const state = this.#state.shirt.find((r) => r.id === rowId);
    if (!state || !state.gender || !state.size || !this.#shirt) return null;
    return (
      this.#shirt.variants.find(
        (v) => v.g === state.gender && v.c === state.size && v.a
      ) || null
    );
  }

  #updateShirtAddButton() {
    const builder = this.#shirtBuilder();
    if (!builder) return;
    const addBtn = builder.querySelector('[ref="shirtAdd"]');
    const addLabel = builder.querySelector('[ref="shirtAddLabel"]');
    if (!addBtn || !addLabel) return;
    const count = this.#state.shirt.length;
    addBtn.hidden = count >= SHIRT_MAX_ROWS;
    addLabel.textContent = count === 0 ? '+ Add a shirt' : '+ Add another shirt';
  }

  // ---------- Render ----------

  #render() {
    const entryVariant = this.#shirtsOnly ? null : this.#resolveVariant('entry');

    const entryRow = this.refs.summary.querySelector('[data-line="entry"]');
    const totalRow = this.refs.summary.querySelector('[data-line="total"]');
    const shirtLinesContainer = this.refs.summary.querySelector('[data-shirt-lines]');
    const shirtEmpty = this.refs.summary.querySelector('[data-line="empty-shirt"]');

    // Entry line (skipped entirely in shirts-only mode — the row isn't rendered).
    if (entryRow) {
      if (entryVariant) {
        const label = this.#entry.hasDivision
          ? `Entry — ${entryVariant.g} · ${entryVariant.d} · ${entryVariant.c}`
          : `Entry — ${entryVariant.g} ${entryVariant.c}`;
        entryRow.querySelector('.nationals-summary__row-label').textContent = label;
        entryRow.querySelector('.nationals-summary__row-price').textContent = this.#money(entryVariant.p);
        entryRow.classList.remove('nationals-summary__row--muted');
      } else {
        const placeholder = this.#entry.hasDivision
          ? 'Entry — choose your division and class'
          : 'Entry — choose your class';
        entryRow.querySelector('.nationals-summary__row-label').textContent = placeholder;
        entryRow.querySelector('.nationals-summary__row-price').textContent = this.#money(this.#entry.price);
        entryRow.classList.add('nationals-summary__row--muted');
      }
    }

    // Shirt lines
    let shirtSubtotal = 0;
    if (this.#shirt && shirtLinesContainer) {
      shirtLinesContainer.replaceChildren();
      const rows = this.#state.shirt;
      const completeRows = [];
      let incompleteCount = 0;

      for (const state of rows) {
        const variant = this.#resolveShirtRow(state.id);
        if (variant) {
          completeRows.push({ state, variant });
        } else {
          incompleteCount += 1;
        }
      }

      // Empty placeholder: visible only when zero rows added in normal mode.
      // In shirts-only mode it's incoherent ("shirt not added" when the shirt
      // IS the entire purchase) — always hide.
      if (shirtEmpty) shirtEmpty.hidden = this.#shirtsOnly || rows.length !== 0;

      // One line per complete row.
      for (const { state, variant } of completeRows) {
        const line = this.#buildSummaryLine(
          `Comp shirt — ${variant.g} ${variant.c} × ${state.qty}`,
          this.#money(variant.p * state.qty),
          false
        );
        shirtLinesContainer.appendChild(line);
        shirtSubtotal += variant.p * state.qty;
      }

      // Collapsed incomplete summary.
      if (incompleteCount === 1) {
        shirtLinesContainer.appendChild(
          this.#buildSummaryLine('Comp shirt — choose gender and size', '', true)
        );
      } else if (incompleteCount > 1) {
        shirtLinesContainer.appendChild(
          this.#buildSummaryLine(`${incompleteCount} shirts not yet complete`, '', true)
        );
      }
    }

    // Total
    const total = (entryVariant ? entryVariant.p : 0) + shirtSubtotal;
    totalRow.querySelector('.nationals-summary__row-price').textContent = this.#money(total);

    // CTA-ready: shirts-only requires at least one complete shirt row;
    // normal mode requires entry resolved.
    const hasCompletedShirt = this.#state.shirt.some((s) => this.#resolveShirtRow(s.id));
    const ready = this.#shirtsOnly ? hasCompletedShirt : !!entryVariant;
    this.refs.cta.disabled = !ready || this.#submitting;
    this.refs.cta.setAttribute('aria-disabled', String(!ready || this.#submitting));
    if (ready && total > 0) {
      this.refs.ctaLabel.textContent = `${this.dataset.ctaLabel || 'Secure My Spot'} — ${this.#money(total)}`;
    } else {
      this.refs.ctaLabel.textContent = this.dataset.ctaLabel || 'Secure My Spot';
    }
  }

  #buildSummaryLine(labelText, priceText, muted) {
    const row = document.createElement('div');
    row.className = 'nationals-summary__row' + (muted ? ' nationals-summary__row--muted nationals-summary__row--strike' : '');
    row.dataset.shirtLine = '';
    const label = document.createElement('span');
    label.className = 'nationals-summary__row-label';
    label.textContent = labelText;
    const price = document.createElement('span');
    price.className = 'nationals-summary__row-price';
    price.textContent = priceText;
    row.appendChild(label);
    row.appendChild(price);
    return row;
  }

  // ---------- Submit ----------

  async #submit() {
    if (this.#submitting) return;

    const items = [];
    if (!this.#shirtsOnly) {
      const entryVariant = this.#resolveVariant('entry');
      if (!entryVariant) return;
      items.push({ id: entryVariant.id, quantity: 1 });
    }
    for (const state of this.#state.shirt) {
      const variant = this.#resolveShirtRow(state.id);
      if (variant) items.push({ id: variant.id, quantity: state.qty });
    }
    // Shirts-only with zero completed rows shouldn't even get here (CTA disabled),
    // but guard defensively.
    if (items.length === 0) return;

    this.#submitting = true;
    this.refs.cta.disabled = true;
    this.refs.status.textContent = 'Adding to cart…';

    // TODO: if APL ever ships localized storefronts, window.Theme.routes.cart_add_url
    // is the locale-aware path (Shopify prefixes the locale, e.g. /en-au/cart/add).
    // The '/cart/add' fallback is fine for the current single-locale store.
    const cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';

    // DO NOT add /cart/clear.js here — Shopify returns a Clear-Site-Data response
    // header on success which breaks the immediately-following /cart/add.js with
    // a 403 Forbidden. Reproduced deterministically across two independent
    // attempts (commits 0b91cc9, 53361f4 — both reverted). Likely related to
    // Shopify CLI issue #7568 filed 2026-05-17 describing identical symptoms in
    // theme dev mode; appears to affect production storefronts too. Accumulation
    // prevention must use a different mechanism — candidates: /cart/update.js
    // with `updates: { variantId: 0 }` to zero out existing lines, or per-item
    // /cart/change.js calls. Neither tested in this context.

    try {
      const addRequest = () =>
        fetch(cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

      // One-shot retry on 403 only — Shopify occasionally rejects the add with a
      // session/CSRF 403 immediately after /cart/clear (transient race). 500ms
      // gives the session cookie state time to settle. Don't retry other codes:
      // a 4xx that isn't 403 is a real client error, a 5xx is a Shopify outage.
      let res = await addRequest();
      if (res.status === 403) {
        console.warn('[nationals-landing] cart add returned 403, retrying once after 500ms');
        await new Promise((resolve) => setTimeout(resolve, 500));
        res = await addRequest();
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || `Cart add failed (${res.status})`);
      }
      try {
        this.dispatchEvent(new CartAddEvent({}, String(items[0].id), { source: 'nationals-landing' }));
      } catch (eventErr) {
        console.warn('[nationals-landing] CartAddEvent dispatch failed (non-blocking)', eventErr);
      }
      window.location.href = '/checkout';
    } catch (err) {
      console.error('[nationals-landing] cart add failed', err);
      this.refs.status.textContent = `Couldn't add to cart: ${err.message}. Try again.`;
      try {
        this.dispatchEvent(new CartErrorEvent('nationals-landing', err.message));
      } catch (eventErr) {
        console.warn('[nationals-landing] CartErrorEvent dispatch failed (non-blocking)', eventErr);
      }
      this.#submitting = false;
      this.refs.cta.disabled = false;
    }
  }

  #money(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.#moneyFormat.replace(/{{\s*amount\s*}}/g, amount);
  }
}

if (!customElements.get('nationals-landing')) {
  customElements.define('nationals-landing', NationalsLanding);
}
